import { AfterContentInit, Component, ContentChildren, EventEmitter, Input, Output, QueryList, TemplateRef } from '@angular/core';
import { AbstractDto, ResultListDto } from '../../service/dto';
import { FormControl } from '@angular/forms';
import { faFilter, faInbox } from '@fortawesome/free-solid-svg-icons';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';


export class OnTableChange {
	filters!: Filter[];
	pageIndex!: number;
	pageSize!: number;
}
export class Filter {
	label!: string;
	type!: 'Text' | 'Date';
	name!: string;
	value?: string;
	format!: string;
}
export class TableColumn {
	title!: string;
	property?: string;
	cellRef?: string;
}
@Component({
	selector: 'app-table',
	templateUrl: './table.component.html',
	styleUrl: './table.component.scss',
	// changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TableComponent<T extends AbstractDto> implements AfterContentInit {
	@Input() queryOnInit = false;
	@Input() headerClass?: string;
	@Input() bodyClass?: string;
	@Input() columns!: TableColumn[];
	@Input() filters: Filter[] = [];
	@Input() loading = false;
	@Input() noDataMessage = 'No data available';
	@Input() emptyDataMessage = 'No data available';
	@Input() showSearchHelp = false;
	@Input() filterModal?: TemplateRef<any>;
	@Input() clickable?: (row: T) => boolean = input => true;
	@Input() disablePaging = false;
	@Input() boldLastRow = false;

	_data?: ResultListDto<T>;
	@Output() onRowClick = new EventEmitter<T>();
	@Output() onRowDblClick = new EventEmitter<T>();
	@Output() onUpdateRequired = new EventEmitter<OnTableChange>();
	@Output() onShowSearchHelpClicked = new EventEmitter<any>();

	@ContentChildren(TemplateRef, { descendants: true }) templates!: QueryList<TemplateRef<any>>;
	templateMap = new Map<string, TemplateRef<any>>();
	formControl = new FormControl('');
	pageIndex?: number;
	pageSize?: number;

	faInbox = faInbox;
	faFilter = faFilter;
	lastPageChange?: NzTableQueryParams;

	constructor(private modalService: NgbModal) {

	}
	ngAfterContentInit() {
		this.templates.forEach((templateRef: any) => {
			const name = templateRef._declarationTContainer?.localNames?.[0]; // Angular internal (fragile)
			if (name) {
				this.templateMap.set(name, templateRef);
			}
		});
	}
	ngOnInit() {
		if (this.queryOnInit) {
			console.log("Emitting for init");
			this.onUpdateRequired.emit({ filters: this.filters, pageIndex: this.pageIndex ?? 0, pageSize: this.pageSize ?? 20 });
		}
	}
	@Input()
	set data(input: ResultListDto<T> | undefined) {
		this._data = input;
		this.pageIndex = input?.pageIndex ?? 0;
		this.pageSize = input?.pageSize ?? 20;
	}
	get data(): ResultListDto<T> | undefined {
		return this._data;
	}


	getTemplate(column: TableColumn): TemplateRef<any> | null {
		if (column.cellRef) {
			return this.templateMap.get(column.cellRef) || null;
		}
		return null;
	}
	getRowValue(row: T, column: TableColumn): any {
		if (!column.property) {
			return undefined;
		}
		// console.log(`Extract Value.  Column:[${column.property}]`, row)
		let value = row as any;
		for (let property of column.property.split(".")) {
			if (value == undefined || value == null) {
				break;
			}
			value = value[property];
		}
		// console.log(`Extracted Value.  Column:[${column.property}]`, value)
		return value;
	}
	trackByRow(index: number, row: AbstractDto): number {
		return row.hashCode;
	}
	trackByColumn(index: number, column: TableColumn): string {
		return column.title + "-" + column.property;
	}


	addFilter(input: Filter) {
		input.value = this.formControl.value!;
		this.formControl.setValue('');
		console.log("Emitting for add filter", input);
		this.onUpdateRequired.emit({ filters: this.filters, pageIndex: this.pageIndex ?? 0, pageSize: this.pageSize ?? 20 });
	}
	editFilter(input: Filter) {
		console.log("Emitting for edit filter", input);
		this.onUpdateRequired.emit({ filters: this.filters, pageIndex: this.pageIndex ?? 0, pageSize: this.pageSize ?? 20 });
	}
	removeFilter(input: Filter) {
		input.value = undefined;
		console.log("Emitting for remove filter", input);
		this.onUpdateRequired.emit({ filters: this.filters, pageIndex: this.pageIndex ?? 0, pageSize: this.pageSize ?? 20 });
	}
	onPageChange(params: NzTableQueryParams) {
		if (!this._data || this._data.list.length == 0) {
			return;
		}
		if (this.lastPageChange && params.pageIndex == this.lastPageChange.pageIndex && params.pageSize == this.lastPageChange.pageSize) {
			return;
		}
		this.lastPageChange = params;
		console.log("Emitting for page change", params);
		this.onUpdateRequired.emit({ filters: this.filters, pageIndex: params.pageIndex, pageSize: params.pageSize ?? 20 });
	}
	allFiltersUsed(): boolean {
		return this.filters.filter(item => item.value == undefined || item.value == null || item.value.trim() == '').length == 0;
	}
	anyFiltersUsed(): boolean {
		return this.filters.filter(item => item.value != undefined && item.value != null && item.value.trim() != '').length > 0;
	}
	isFormFilterValueInvalid(filter: Filter): boolean {
		return this.isFilterValueInvalid(filter, this.formControl.value);
	}
	isFilterValueInvalid(filter: Filter, value: string | null): boolean {

		if (value === null || value === undefined || value === '') {
			// console.log(`Filter invalid.  Name:[${filter.name}] Reason:[No value]`, value)
			return true; // empty values are always invalid
		}

		// Apply regex if format is defined
		if (filter.format) {
			try {
				if (!new RegExp(filter.format).test(value)) {
					return true; // does not match format
				}
			} catch (e) {
				console.warn(`Invalid regex format in filter: ${filter.format}`, e);
				return true;
			}
		}
		if (filter.type == 'Text') {
			return false;
		}
		if (filter.type == 'Date') {
			const date = new Date(value);
			return isNaN(date.getTime());
		}
		return true;
	}
	showFilterModal() {
		const beforeValue = JSON.parse(JSON.stringify(this.filters)) as Filter[];

		const modal = this.modalService.open(this.filterModal!, { size: 'lg' });
		modal.result.then(
			() => this.updateFilters(beforeValue),
			() => this.updateFilters(beforeValue)
		);
	}
	updateFilters(beforeValue: Filter[]) {
		let filersChanged: boolean = false;

		this.filters.forEach(item => {
			const previousState = beforeValue.filter(i => i.name == item.name)[0];

			if (this.isFilterValueInvalid(item, item.value!)) {
				console.log('Filter is invalid.', item, previousState)
				item.value = previousState?.value ?? '';
				// item.value = '';
			}
			if (filersChanged == false || (previousState?.value ?? '') == item.value) {
				filersChanged = true;
			}
		});
		if (filersChanged) {
			this.onUpdateRequired.emit({ filters: this.filters, pageIndex: this.pageIndex ?? 0, pageSize: this.pageSize ?? 20 });
		}
	}
	onClick(input: T) {
		if (this.isClickable(input)) {
			this.onRowClick.emit(input);
		}
	}
	onDblClick(input: T) {
		if (this.isClickable(input)) {
			this.onRowDblClick.emit(input);
		}
	}
	isClickable(input: T) {
		if (this.clickable == undefined) {
			return true;
		}
		return this.clickable(input);
	}
}
